// P7 reads and writes (CLS-C): row-level-secured views for reads and idempotent commands for writes.
import { Refusal } from './auth';
import { sendCommand } from './commands';
import { supabase } from './supabase';

export const EXPENSES = ['expenses'] as const;

export interface ExpenseRow {
  id: string;
  number: string;
  category: string;
  /** The application's own categories (fixed wage, pickup costs) are translated; others show as typed. */
  category_key: string | null;
  business_date: string;
  at: string;
  source: 'round' | 'garage' | 'bank' | 'personal';
  truck_id: string | null;
  truck_code: string | null;
  round_id: string | null;
  round_number: string | null;
  notes: string | null;
  amount: number;
  in_stock_cost: boolean;
  correction: boolean;
  entry_kind: string;
  corrects_id: string | null;
  cash_move_id: number | null;
  correctable: boolean;
}

export interface ExpenseCategory {
  id: string;
  name: string;
  category_key: string | null;
  active: boolean;
  fixed: boolean;
}

export interface SellerBalance {
  seller_id: string;
  seller_name: string;
  active: boolean;
  trucks: string;
  round_cash: number;
  wages_total: number;
  balance: number;
  unpaid_invoice_count: number;
  unpaid_invoice_total: number;
}

export interface RoundClose {
  round_id: string;
  number: string;
  truck_id: string;
  truck_code: string;
  seller_id: string;
  seller_name: string;
  started_at: string;
  selling_ended_at: string;
  closed_at: string;
  status: string;
  snapshot: Record<string, unknown>;
  paid_total: number;
  profit_total: number | null;
  expenses_total: number;
  expected_handover: number;
  handed_over: number;
  variance: number;
  variance_reason: string | null;
}

export interface SellerLedgerRow {
  id: number;
  /** The record the entry comes from (EXP-…, RND-…). */
  reference_number: string | null;
  seller_id: string;
  seller_name: string;
  entry_kind: string;
  amount: number;
  business_date: string;
  reference_type: string;
  reference_id: string;
  notes: string | null;
  at: string;
  actor_name: string | null;
}

function rows<T>(res: { data: unknown; error: { message: string } | null }): T[] {
  if (res.error) throw new Refusal('SERVER_ERROR');
  return (res.data ?? []) as T[];
}

export async function loadExpenses(): Promise<ExpenseRow[]> {
  return rows<ExpenseRow>(
    await supabase.from('v_expenses').select('*').order('business_date', { ascending: false }).limit(300),
  );
}

export async function loadRoundExpenses(round: string): Promise<ExpenseRow[]> {
  return rows<ExpenseRow>(
    await supabase
      .from('v_expenses')
      .select('*')
      .eq('round_id', round)
      .order('business_date', { ascending: false })
      .order('at', { ascending: false }),
  );
}

export async function loadExpenseCategories(): Promise<ExpenseCategory[]> {
  return rows<ExpenseCategory>(
    await supabase.from('v_expense_categories').select('id,name,active,fixed').order('name'),
  );
}

export async function loadSellerBalances(): Promise<SellerBalance[]> {
  return rows<SellerBalance>(await supabase.from('v_seller_balances').select('*').order('seller_name'));
}

export async function loadRoundClose(round: string): Promise<RoundClose | null> {
  return rows<RoundClose>(await supabase.from('v_round_close').select('*').eq('round_id', round))[0] ?? null;
}

export async function loadSellerRoundCloses(seller: string): Promise<RoundClose[]> {
  return rows<RoundClose>(
    await supabase.from('v_round_close').select('*').eq('seller_id', seller).order('closed_at', { ascending: false }),
  );
}

export async function loadTruckRoundCloses(truck: string): Promise<RoundClose[]> {
  return rows<RoundClose>(
    await supabase
      .from('v_round_close')
      .select('*')
      .eq('truck_id', truck)
      .order('closed_at', { ascending: false })
      .limit(30),
  );
}

export async function loadSellerLedger(seller: string): Promise<SellerLedgerRow[]> {
  return rows<SellerLedgerRow>(
    await supabase
      .from('v_seller_ledger')
      .select('*')
      .eq('seller_id', seller)
      .order('business_date', { ascending: false })
      .order('id', { ascending: false }),
  );
}

export const recordExpense = (input: {
  category: string;
  amount: number;
  funding: 'garage' | 'bank' | 'personal';
  date: string;
  truck: string | null;
  payer: string | null;
  notes: string | null;
}) =>
  sendCommand('record_expense', {
    p_category: input.category,
    p_amount: input.amount,
    p_funding: input.funding,
    p_date: input.date,
    p_truck: input.truck,
    p_payer: input.payer,
    p_notes: input.notes,
  });

export const correctExpense = (expense: string, amount: number, description: string) =>
  sendCommand('correct_expense', { p_expense: expense, p_amount: amount, p_description: description });

export const saveExpenseCategory = (name: string) => sendCommand('save_expense_category', { p_name: name });
export const toggleExpenseCategory = (category: string, active: boolean) =>
  sendCommand('toggle_expense_category', { p_category: category, p_active: active });
export const settleSeller = (seller: string, amount: number) =>
  sendCommand('settle_seller', { p_seller: seller, p_amount: amount });

export const reassignUnpaid = (from: string, sales: string[], to: string, reason: string) =>
  sendCommand('reassign_unpaid', { p_from: from, p_sales: sales, p_to: to, p_reason: reason });

export const closeRound = (input: {
  round: string;
  expenses: { category: string; amount: number }[];
  wage: number;
  handedOver: number;
  varianceReason: string | null;
  shortageTo: 'seller' | 'business';
}) =>
  sendCommand('close_round', {
    p_round: input.round,
    p_expenses: input.expenses.map((line) => ({ category: line.category, amount: line.amount })),
    p_wage: input.wage,
    p_handed_over: input.handedOver,
    p_variance_reason: input.varianceReason,
    p_shortage_to: input.shortageTo,
  });

/** A category's label: the application's own categories (fixed wage, pickup costs) in the reader's language. */
export function categoryLabel(t: (key: string) => string, name: string, key: string | null | undefined): string {
  return key ? t(`p7.cat.${key}`) : name;
}
